import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Hexagon, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../contexts/AuthContext";
import { useBranding } from "../contexts/BrandingContext";
import { authApi, formatApiError } from "../lib/ledgerApi";

export default function Login() {
  const { setSession } = useAuth();
  const { branding } = useBranding();
  const nav = useNavigate();
  const location = useLocation();
  const next = new URLSearchParams(location.search).get("next") || "/app/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const data = await authApi.login(email, password);
      setSession(data);
      nav(next);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const fillDemo = (mail) => {
    setEmail(mail);
    setPassword("demo-pass-123");
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background text-foreground">
      <div className="hidden md:flex flex-col justify-between p-10 border-r border-border bg-card grid-line-bg">
        <Link to="/" className="flex items-center gap-2 font-medium">
          {branding.logo_url ? (
            <img src={branding.logo_url} alt={branding.product_name} className="w-8 h-8 object-contain rounded-sm border border-foreground/40" />
          ) : (
            <span className="w-8 h-8 inline-flex items-center justify-center border border-foreground/40 rounded-sm">
              <Hexagon className="w-4 h-4" strokeWidth={1.5} />
            </span>
          )}
          <span>{branding.product_name}</span>
        </Link>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">B2B payments</div>
          <h1 className="text-4xl tracking-tighter font-medium leading-[1.05]">
            Move money on a ledger you can audit, not a black box.
          </h1>
          <p className="mt-4 text-sm text-muted-foreground max-w-md leading-relaxed">
            VPA-routed transfers across NEO + 6 fiat currencies. Every send is a signed,
            Merkle-anchored transaction on the {branding.product_name} chain.
          </p>
        </div>
        <div className="text-xs font-mono text-muted-foreground">
          Phase 1 · Core Payment Rail
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">/ Sign in</div>
            <h2 className="text-3xl tracking-tight font-medium">Welcome back</h2>
            <p className="text-sm text-muted-foreground mt-2">Use your business email and password.</p>
          </div>

          {error && (
            <div data-testid="login-error" className="border border-status-tampered/40 bg-status-tampered/10 p-3 rounded-sm text-status-tampered text-xs font-mono">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
            <Input
              type="email"
              autoComplete="email"
              required
              data-testid="login-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-sm font-mono"
              placeholder="admin@acme.demo"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Password</Label>
            <Input
              type="password"
              autoComplete="current-password"
              required
              data-testid="login-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-sm font-mono"
            />
          </div>

          <Button type="submit" disabled={busy} data-testid="login-submit" className="rounded-sm w-full">
            {busy ? "Signing in…" : (<span className="inline-flex items-center gap-2">Sign in <ArrowRight className="w-4 h-4" strokeWidth={1.5}/></span>)}
          </Button>

          <div className="text-xs text-muted-foreground">
            No account?{" "}
            <Link to="/signup" data-testid="link-signup" className="underline hover:text-foreground">
              Create a business
            </Link>
          </div>

          <div className="border-t border-border pt-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Demo accounts</div>
            <div className="flex flex-wrap gap-2 text-xs font-mono">
              {[
                { e: "admin@acme.demo", n: "Acme" },
                { e: "admin@globex.demo", n: "Globex" },
                { e: "admin@initech.demo", n: "Initech" },
                { e: "admin@neopay.io", n: "Super-admin", p: "neopay-admin" },
              ].map((d) => (
                <button
                  key={d.e}
                  type="button"
                  onClick={() => {
                    setEmail(d.e);
                    setPassword(d.p || "demo-pass-123");
                  }}
                  data-testid={`demo-login-${d.n}`}
                  className="px-2 py-1 border border-border rounded-sm hover:border-foreground transition-colors"
                >
                  {d.n}
                </button>
              ))}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
